import type { ReactNode } from 'react'

import { cn } from '@jotta/utils/css'

import type {
  PrimaryStrokeClassName,
  SecondaryStrokeClassName,
} from '../../StrokeClassNameType'

export type CircularProgressProps = {
  progress: number
  size?: 'small' | 'medium' | 'large'
  primaryStrokeClassName?: PrimaryStrokeClassName
  secondaryStrokeClassName?: SecondaryStrokeClassName
  children?: ReactNode
}

/**
 * Material Design 3 style circular progress indicator.
 */
export const CircularProgress = ({
  progress = 0,
  size,
  primaryStrokeClassName = 'stroke-informative',
  secondaryStrokeClassName = 'stroke-on-primary',
  children,
}: CircularProgressProps) => {
  const viewBoxSize = 48
  const center = viewBoxSize / 2
  const strokeWidth = 3
  const radius = (viewBoxSize - strokeWidth) / 2

  const isFullCircle = progress <= 0 || progress >= 1
  const effectiveProgress = Math.min(Math.max(progress, 0), 1)

  const gapAngle = 20
  const availableAngle = 360 - gapAngle * 2

  const progressAngle = effectiveProgress * availableAngle
  const remainingAngle = availableAngle - progressAngle

  const progressStart = gapAngle
  const progressEnd = progressStart + progressAngle

  const backgroundStart = progressEnd + gapAngle
  const backgroundEnd = backgroundStart + remainingAngle

  const rotationOffset = -90 - gapAngle / 2
  const hasCenterContent = children !== undefined && children !== null

  return (
    <div
      className={cn('relative h-full w-full', {
        'h-14 w-14': size === 'large',
        'h-8 w-8': size === 'medium',
        'h-5 w-5': size === 'small',
      })}
    >
      <svg
        className="h-full w-full"
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="progressbar"
        aria-valuenow={Math.round(effectiveProgress * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {isFullCircle ? (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            className={cn(
              progress <= 0 ? secondaryStrokeClassName : primaryStrokeClassName,
            )}
          />
        ) : (
          <g
            style={{
              transformOrigin: `${center}px ${center}px`,
              transform: `rotate(${rotationOffset}deg)`,
            }}
          >
            {remainingAngle > 0 && (
              <path
                d={createArcPath(
                  center,
                  center,
                  radius,
                  backgroundStart,
                  backgroundEnd,
                )}
                fill="none"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                className={secondaryStrokeClassName}
              />
            )}
            <path
              d={createArcPath(
                center,
                center,
                radius,
                progressStart,
                progressEnd,
              )}
              fill="none"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              className={primaryStrokeClassName}
            />
          </g>
        )}
      </svg>

      {hasCenterContent && (
        <div className="absolute inset-0 flex items-center justify-center">
          {children}
        </div>
      )}
    </div>
  )
}

function createArcPath(
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string {
  const delta = endAngle - startAngle
  const start = angleToPoint(centerX, centerY, radius, startAngle)
  const large = 0
  const sweep = 1
  if (delta <= 0) {
    const sx = start.x
    const sy = start.y
    return `M${sx} ${sy} A${radius} ${radius} 0 ${large} ${sweep} ${sx} ${sy} A${radius} ${radius} 0 ${large} ${sweep} ${sx} ${sy}`
  }
  const splitPoint = Math.min(delta, 180)
  const midAngle = startAngle + splitPoint
  const mid = angleToPoint(centerX, centerY, radius, midAngle)
  const end = angleToPoint(centerX, centerY, radius, endAngle)
  return `M${start.x} ${start.y} A${radius} ${radius} 0 ${large} ${sweep} ${mid.x} ${mid.y} A${radius} ${radius} 0 ${large} ${sweep} ${end.x} ${end.y}`
}

function angleToPoint(
  centerX: number,
  centerY: number,
  radius: number,
  angleDegrees: number,
): { x: number; y: number } {
  const angleRadians = (angleDegrees * Math.PI) / 180
  return {
    x: centerX + radius * Math.cos(angleRadians),
    y: centerY + radius * Math.sin(angleRadians),
  }
}

export default CircularProgress
